import React, { useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import StackedLineChartRoundedIcon from '@mui/icons-material/StackedLineChartRounded';
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  BarElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { marketingPalette } from '../../content/marketing';
import { publicBodyFontFamily, publicHeadingFontFamily } from '../common/PublicPageFrame';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const palette = marketingPalette;

const surfaceSx = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: { xs: '26px', md: '30px' },
  border: `1px solid ${alpha(palette.ink, 0.08)}`,
  bgcolor: alpha('#ffffff', 0.94),
  boxShadow: '0 28px 72px rgba(18, 61, 103, 0.08)',
};

const categoryLabels = {
  historyQuestions: 'Clinical history',
  flow: 'Communication flow',
  examinerQuestions: 'Examiner questions',
  empathy: 'Empathy',
};

function calculateRank(score) {
  if (score >= 90) return 'Exam-ready';
  if (score >= 80) return 'Strong';
  if (score >= 70) return 'Developing well';
  if (score >= 60) return 'Building';
  return 'Early stage';
}

function formatSignedDelta(value) {
  if (!Number.isFinite(value) || value === 0) {
    return 'Steady';
  }

  return `${value > 0 ? '+' : ''}${value.toFixed(1)} pts`;
}

function StatCard({ title, value, description, icon, tone = 'default' }) {
  const tones = {
    default: {
      bg: alpha(palette.heroBlueMist, 0.56),
      iconBg: alpha(palette.heroBlueMid, 0.10),
      iconColor: palette.heroBlueDeep,
      valueColor: palette.ink,
    },
    strong: {
      bg: alpha(palette.heroBlue, 0.10),
      iconBg: alpha(palette.heroBlueDeep, 0.10),
      iconColor: palette.heroBlueDeep,
      valueColor: palette.heroBlueDeep,
    },
  };

  const resolvedTone = tones[tone] || tones.default;

  return (
    <Box
      sx={{
        borderRadius: '24px',
        border: `1px solid ${alpha(palette.ink, 0.07)}`,
        bgcolor: resolvedTone.bg,
        p: 2.3,
        height: '100%',
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography
            sx={{
              color: palette.inkSoft,
              fontSize: '0.8rem',
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontFamily: publicBodyFontFamily,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 0.8,
              color: resolvedTone.valueColor,
              fontSize: { xs: '1.45rem', md: '1.65rem' },
              lineHeight: 1.05,
              fontWeight: 900,
              letterSpacing: '-0.04em',
              fontFamily: publicHeadingFontFamily,
            }}
          >
            {value}
          </Typography>
          <Typography
            sx={{
              mt: 0.9,
              color: palette.inkSoft,
              lineHeight: 1.6,
              fontSize: '0.92rem',
              fontFamily: publicBodyFontFamily,
            }}
          >
            {description}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '18px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: resolvedTone.iconBg,
            color: resolvedTone.iconColor,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Box>
  );
}

function ChartCard({ eyebrow, title, description, children }) {
  return (
    <Box sx={{ ...surfaceSx, p: { xs: 2.4, md: 3 } }}>
      <Typography
        sx={{
          color: palette.heroBlueMid,
          fontSize: '0.8rem',
          fontWeight: 800,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontFamily: publicBodyFontFamily,
        }}
      >
        {eyebrow}
      </Typography>
      <Typography
        sx={{
          mt: 0.8,
          color: palette.ink,
          fontSize: { xs: '1.3rem', md: '1.55rem' },
          lineHeight: 1.1,
          letterSpacing: '-0.03em',
          fontFamily: publicHeadingFontFamily,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          mt: 0.9,
          mb: 2.4,
          color: palette.inkSoft,
          lineHeight: 1.68,
          fontFamily: publicBodyFontFamily,
        }}
      >
        {description}
      </Typography>
      {children}
    </Box>
  );
}

function EmptyChartState({ title, description }) {
  return (
    <Box
      sx={{
        minHeight: 280,
        borderRadius: '22px',
        border: `1px dashed ${alpha(palette.ink, 0.14)}`,
        bgcolor: alpha(palette.heroBlueMist, 0.65),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
      }}
    >
      <Typography
        sx={{
          color: palette.ink,
          fontWeight: 800,
          mb: 0.8,
          fontFamily: publicBodyFontFamily,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          maxWidth: 380,
          color: palette.inkSoft,
          lineHeight: 1.68,
          fontFamily: publicBodyFontFamily,
        }}
      >
        {description}
      </Typography>
    </Box>
  );
}

function PerformanceOverview({ statistics }) {
  const categoryEntries = useMemo(() => (
    Object.entries(statistics?.categoryAverages || {})
      .map(([key, value]) => ({
        key,
        label: categoryLabels[key] || key,
        value: Number(value) || 0,
      }))
  ), [statistics?.categoryAverages]);

  const strongestCategory = useMemo(() => {
    if (!categoryEntries.length) {
      return null;
    }

    return [...categoryEntries].sort((left, right) => right.value - left.value)[0];
  }, [categoryEntries]);

  const weakestCategory = useMemo(() => {
    if (!categoryEntries.length) {
      return null;
    }

    return [...categoryEntries].sort((left, right) => left.value - right.value)[0];
  }, [categoryEntries]);

  const chronologicalHistories = useMemo(() => (
    Array.isArray(statistics?.recentHistories)
      ? [...statistics.recentHistories].reverse()
      : []
  ), [statistics?.recentHistories]);

  const latestScore = Number(statistics?.recentHistories?.[0]?.overallPercentage) || 0;

  const trendDelta = useMemo(() => {
    if (!statistics?.recentHistories || statistics.recentHistories.length < 2) {
      return 0;
    }

    const previousScores = statistics.recentHistories
      .slice(1)
      .map((item) => Number(item?.overallPercentage) || 0);

    if (!previousScores.length) {
      return 0;
    }

    const previousAverage = previousScores.reduce((sum, value) => sum + value, 0) / previousScores.length;
    return latestScore - previousAverage;
  }, [latestScore, statistics?.recentHistories]);

  const lineData = useMemo(() => ({
    labels: chronologicalHistories.map((item) => new Date(item.completedAt).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
    })),
    datasets: [
      {
        label: 'Score',
        data: chronologicalHistories.map((item) => Number(item?.overallPercentage) || 0),
        borderColor: palette.heroBlueMid,
        backgroundColor: alpha(palette.heroBlue, 0.18),
        fill: true,
        tension: 0.32,
        pointRadius: 4,
        pointHoverRadius: 5,
        pointBackgroundColor: palette.heroBlueDeep,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
      },
    ],
  }), [chronologicalHistories]);

  const barData = useMemo(() => ({
    labels: categoryEntries.map((entry) => entry.label),
    datasets: [
      {
        label: 'Average score',
        data: categoryEntries.map((entry) => entry.value),
        backgroundColor: [
          alpha(palette.heroBlueDeep, 0.88),
          alpha(palette.heroBlueMid, 0.84),
          alpha(palette.heroBlue, 0.78),
          alpha(palette.heroBlueStrong, 0.74),
        ],
        borderRadius: 12,
        borderSkipped: false,
      },
    ],
  }), [categoryEntries]);

  const lineOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(18, 61, 103, 0.92)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        displayColors: false,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: palette.inkSoft,
          font: {
            family: 'Manrope',
            size: 11,
          },
        },
      },
      y: {
        min: 0,
        max: 100,
        grid: {
          color: alpha(palette.ink, 0.08),
        },
        ticks: {
          color: palette.inkSoft,
          callback: (value) => `${value}%`,
          font: {
            family: 'Manrope',
            size: 11,
          },
        },
      },
    },
  }), []);

  const barOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(18, 61, 103, 0.92)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        displayColors: false,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: palette.inkSoft,
          maxRotation: 0,
          minRotation: 0,
          font: {
            family: 'Manrope',
            size: 11,
          },
        },
      },
      y: {
        min: 0,
        max: 100,
        grid: {
          color: alpha(palette.ink, 0.08),
        },
        ticks: {
          color: palette.inkSoft,
          callback: (value) => `${value}%`,
          font: {
            family: 'Manrope',
            size: 11,
          },
        },
      },
    },
  }), []);

  return (
    <Stack spacing={3}>
      <Box sx={{ ...surfaceSx, p: { xs: 2.4, md: 3 } }}>
        <Stack spacing={1}>
          <Typography
            sx={{
              color: palette.heroBlueMid,
              fontSize: '0.8rem',
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontFamily: publicBodyFontFamily,
            }}
          >
            Performance overview
          </Typography>
          <Typography
            sx={{
              color: palette.ink,
              fontSize: { xs: '1.45rem', md: '1.75rem' },
              lineHeight: 1.08,
              letterSpacing: '-0.03em',
              fontFamily: publicHeadingFontFamily,
            }}
          >
            Your recent consultation performance
          </Typography>
          <Typography
            sx={{
              color: palette.inkSoft,
              lineHeight: 1.72,
              fontFamily: publicBodyFontFamily,
            }}
          >
            Keep track of score trends, stronger sections, and the areas that need the next round of focused practice.
          </Typography>
        </Stack>

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <StatCard
              title="Completed cases"
              value={String(statistics?.totalHistories || 0)}
              description="All marked consultations saved to your account."
              icon={<QueryStatsRoundedIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StatCard
              title="Average score"
              value={`${(Number(statistics?.averageScore) || 0).toFixed(1)}%`}
              description={`Current standing: ${calculateRank(Number(statistics?.averageScore) || 0)}.`}
              icon={<WorkspacePremiumRoundedIcon />}
              tone="strong"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StatCard
              title="Strongest area"
              value={strongestCategory ? strongestCategory.label : 'No data yet'}
              description={strongestCategory ? `${strongestCategory.value.toFixed(1)}% average across completed cases.` : 'Complete a few cases to surface your strongest consultation area.'}
              icon={<CheckCircleOutlineRoundedIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StatCard
              title="Next focus"
              value={weakestCategory ? weakestCategory.label : 'No data yet'}
              description={weakestCategory ? `${weakestCategory.value.toFixed(1)}% average. This is the clearest area to target next.` : 'Once you have scored sections, your next focus area will show here.'}
              icon={<TrackChangesRoundedIcon />}
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <ChartCard
            eyebrow="Trend"
            title="Recent scores"
            description={`Latest result: ${latestScore.toFixed(1)}%. Change vs earlier recent cases: ${formatSignedDelta(trendDelta)}.`}
          >
            {chronologicalHistories.length > 0 ? (
              <Box sx={{ height: 320 }}>
                <Line data={lineData} options={lineOptions} />
              </Box>
            ) : (
              <EmptyChartState
                title="No trend data yet"
                description="Complete and mark a case to start seeing your recent score trend."
              />
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} lg={5}>
          <ChartCard
            eyebrow="Sections"
            title="Average section scores"
            description="A quick view of how your scoring currently varies across consultation domains."
          >
            {categoryEntries.some((entry) => entry.value > 0) ? (
              <Box sx={{ height: 320 }}>
                <Bar data={barData} options={barOptions} />
              </Box>
            ) : (
              <EmptyChartState
                title="No section averages yet"
                description="Section-level averages will appear after your first marked case."
              />
            )}
          </ChartCard>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ ...surfaceSx, p: { xs: 2.4, md: 3 }, height: '100%' }}>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.4 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '16px',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(palette.heroBlueDeep, 0.10),
                  color: palette.heroBlueDeep,
                }}
              >
                <AutoGraphRoundedIcon />
              </Box>
              <Typography
                sx={{
                  color: palette.ink,
                  fontSize: { xs: '1.2rem', md: '1.35rem' },
                  lineHeight: 1.1,
                  fontFamily: publicHeadingFontFamily,
                }}
              >
                What the trend suggests
              </Typography>
            </Stack>
            <Typography
              sx={{
                color: palette.inkSoft,
                lineHeight: 1.78,
                fontFamily: publicBodyFontFamily,
              }}
            >
              {!statistics?.totalHistories
                ? 'Once you have completed a few consultations, this panel will help interpret whether your scores are stabilising or starting to climb.'
                : trendDelta > 2
                  ? 'Your latest score is moving above your recent baseline. Keep the same practice rhythm and review what changed in your last stronger case.'
                  : trendDelta < -2
                    ? 'Your latest score dipped below your recent baseline. Review the weaker sections before your next consultation to stop that slip becoming a pattern.'
                    : 'Your recent performance is relatively steady. The best next step is to target your weakest section rather than simply repeating similar cases.'}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ ...surfaceSx, p: { xs: 2.4, md: 3 }, height: '100%' }}>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.4 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '16px',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(palette.heroBlue, 0.14),
                  color: palette.heroBlueDeep,
                }}
              >
                <StackedLineChartRoundedIcon />
              </Box>
              <Typography
                sx={{
                  color: palette.ink,
                  fontSize: { xs: '1.2rem', md: '1.35rem' },
                  lineHeight: 1.1,
                  fontFamily: publicHeadingFontFamily,
                }}
              >
                Recommended focus
              </Typography>
            </Stack>
            <Typography
              sx={{
                color: palette.inkSoft,
                lineHeight: 1.78,
                fontFamily: publicBodyFontFamily,
              }}
            >
              {weakestCategory
                ? `Prioritise ${weakestCategory.label.toLowerCase()} next. It is currently your lowest-scoring section at ${weakestCategory.value.toFixed(1)}%, so even one stronger case there should lift your overall performance fastest.`
                : 'Once you have more scored consultations, the dashboard will surface the section most likely to improve your overall score next.'}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
}

export default PerformanceOverview;
